import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Smartphone, Laptop, Headphones, Watch } from 'lucide-react'
import { productService } from '../services/productService'
import ProductCard from '../components/ProductCard'
import LoadingSpinner from '../components/LoadingSpinner'

const Home = () => {
  const { data: featuredProducts, isLoading: featuredLoading } = useQuery({
    queryKey: ['featured-products'],
    queryFn: productService.getFeaturedProducts,
  })

  const categories = [
    {
      name: 'Smartphones',
      icon: Smartphone,
      link: '/products?category=mobiles',
      description: 'Latest smartphones with cutting-edge technology',
    },
    {
      name: 'Laptops',
      icon: Laptop,
      link: '/products?category=laptops',
      description: 'High-performance laptops for work and gaming',
    },
    {
      name: 'Audio',
      icon: Headphones,
      link: '/products?category=audio',
      description: 'Premium headphones and speakers',
    },
    {
      name: 'Wearables',
      icon: Watch,
      link: '/products?category=wearables',
      description: 'Smart watches and fitness trackers',
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center text-white overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://sleevecityusa.com/cdn/shop/files/easy-listening_1296x.jpg?v=1613710322"
            alt="Electronics store with modern devices"
            className="w-full h-full object-cover"
          />
          {/* Subtle dark overlay only for text readability */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 text-center">
          <div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight font-sans antialiased">
              <span className="inline-block text-white drop-shadow-2xl">
                Premium Electronics
              </span>
              <span className="block bg-gradient-to-r from-primary-400 via-secondary-400 to-accent-400 bg-clip-text text-transparent mt-2">
                At Your Fingertips
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto leading-relaxed drop-shadow-lg font-sans antialiased">
              Discover the latest smartphones, laptops, and accessories from top brands. 
              Quality guaranteed, prices unmatched.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/products"
                className="group btn bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:from-primary-600 hover:to-secondary-600 px-8 py-4 text-lg font-semibold shadow-2xl transition-all duration-300"
              >
                Shop Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/products?featured=true"
                className="group btn border-2 border-white bg-white/10 backdrop-blur-sm text-white hover:bg-white hover:text-primary-600 px-8 py-4 text-lg font-semibold shadow-2xl transition-all duration-300"
              >
                View Deals
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
            
            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg font-sans antialiased">1000+</div>
                <div className="text-white/80 text-sm md:text-base font-sans antialiased">Products</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg font-sans antialiased">50+</div>
                <div className="text-white/80 text-sm md:text-base font-sans antialiased">Brands</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg font-sans antialiased">24/7</div>
                <div className="text-white/80 text-sm md:text-base font-sans antialiased">Support</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg font-sans antialiased">99%</div>
                <div className="text-white/80 text-sm md:text-base font-sans antialiased">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
        

      </section>

      {/* Categories Section */}
      <section className="py-16 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
              Shop by Category
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find exactly what you&apos;re looking for in our carefully curated categories
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => {
              const IconComponent = category.icon
              return (
                <Link
                  key={category.name}
                  to={category.link}
                  className="group card p-6 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-white to-primary-50"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {category.description}
                  </p>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
                Featured Products
              </h2>
              <p className="text-lg text-gray-600">
                Handpicked products with the best value and latest technology
              </p>
            </div>
            <Link
              to="/products?featured=true"
              className="btn btn-outline px-6 py-2 flex items-center border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white"
            >
              View All
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>

          {featuredLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts?.slice(0, 8).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/products?featured=true"
              className="btn btn-outline px-6 py-2 inline-flex items-center"
            >
              View All Featured Products
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Best Deals Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Best Deals
            </h2>
            <p className="text-lg text-gray-600">
              Limited time offers on premium electronics
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Deal 1 */}
            <div className="card overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img
                    src="https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&h=300&fit=crop"
                    alt="Smartphone Deal"
                    className="w-full h-48 md:h-full object-cover"
                  />
                </div>
                <div className="p-6 md:w-1/2 flex flex-col justify-center">
                  <div className="text-sm font-semibold text-primary-600 mb-2">
                    LIMITED TIME
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Up to 30% Off
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Latest smartphones from top brands
                  </p>
                  <Link
                    to="/products?category=mobiles&sale=true"
                    className="btn btn-primary w-fit"
                  >
                    Shop Smartphones
                  </Link>
                </div>
              </div>
            </div>

            {/* Deal 2 */}
            <div className="card overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img
                    src="https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=400&h=300&fit=crop"
                    alt="Laptop Deal"
                    className="w-full h-48 md:h-full object-cover"
                  />
                </div>
                <div className="p-6 md:w-1/2 flex flex-col justify-center">
                  <div className="text-sm font-semibold text-primary-600 mb-2">
                    SPECIAL OFFER
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Gaming Laptops
                  </h3>
                  <p className="text-gray-600 mb-4">
                    High-performance laptops for gaming and work
                  </p>
                  <Link
                    to="/products?category=laptops&type=gaming"
                    className="btn btn-primary w-fit"
                  >
                    Shop Laptops
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Stay Updated
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Get the latest deals, product launches, and tech news delivered to your inbox
          </p>
          <div className="max-w-md mx-auto flex gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="input flex-1 border-white/20 bg-white/10 backdrop-blur-sm text-white placeholder:text-white/70"
            />
            <button className="btn bg-white text-primary-600 hover:bg-gray-100 px-6 shadow-lg">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
